// Audit Logs: pure helpers for one automation_log row ({ id, ts, type, meta }).
//
// Everything here reads only what the backend actually wrote (see
// backend/server/brain/executePlan.js, server/automationWorker.js and
// server/meetingNotes/*): meta.backend for task tools, meta.chatBackend for
// chat, meta.channels for escalations, meta.provider for calendars and
// meetings, meta.action / meta.result for approvals. A row whose fields do
// not name a tool gets no tool, never a guess.
//
// No em dashes in anything here that reaches the screen.

/* ---------------------------------------------------------------- basics */

export function logMeta(log) {
  return log?.meta && typeof log.meta === "object" ? log.meta : {};
}

function str(v) {
  return v == null ? "" : String(v).trim();
}

function lower(v) {
  return str(v).toLowerCase();
}

function plural(n, one, many = `${one}s`) {
  return `${n} ${Number(n) === 1 ? one : many}`;
}

/** "duplicate_fingerprint" -> "duplicate fingerprint". */
export function humanize(value) {
  return str(value)
    .replace(/[_:]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function capitalize(text) {
  const t = str(text);
  return t ? t[0].toUpperCase() + t.slice(1) : t;
}

/** Shorten an error or reason string for a one-line context. */
export function shortText(value, max = 90) {
  const t = str(value).replace(/\s+/g, " ");
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/**
 * A delivery error code as words: "dmTeams_user_not_found(to=a@b.c)" ->
 * "User not found". Free text is only shortened.
 */
export function friendlyReason(value, max = 70) {
  let t = str(value)
    .replace(/\((?:to|email)=[^)]*\)/gi, "")
    .replace(/^dm(?:Teams|Slack)_/i, "")
    .trim();
  if (/^[a-z0-9_:-]+$/i.test(t)) t = capitalize(humanize(t));
  return shortText(t, max);
}

/* ------------------------------------------------------------ type tests */

export function isFailureType(type) {
  const t = lower(type);
  return t.includes("_failed") || t.includes("_error");
}

export function isApprovalType(type) {
  return lower(type).startsWith("approval_");
}

export function isSkippedType(type) {
  return lower(type).includes("skipped");
}

export function isWarningType(type) {
  return lower(type).includes("_warning");
}

export function isDecisionType(type) {
  const t = lower(type);
  return t === "tick" || t.startsWith("brain_");
}

export function isExecutedType(type) {
  const t = lower(type);
  if (!t) return false;
  if (isFailureType(t) || isSkippedType(t)) return false;
  if (t === "approval_executed") return true;
  return /(_sent|_created|_scheduled|_updated|_rescheduled)$/.test(t);
}

/* ---------------------------------------------------------------- counts */

function pickCount(...vals) {
  for (const v of vals) {
    if (typeof v === "number" && Number.isFinite(v)) {
      return Math.max(0, Math.round(v));
    }
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) return Math.max(0, Math.round(n));
    }
  }
  return null;
}

function readCountFromLog(log, keys = [], arrayKeys = []) {
  const m = logMeta(log);
  for (const k of keys) {
    const nMeta = pickCount(m?.[k]);
    if (nMeta != null) return nMeta;
    const nTop = pickCount(log?.[k]);
    if (nTop != null) return nTop;
  }
  for (const k of arrayKeys) {
    if (Array.isArray(m?.[k])) return m[k].length;
    if (Array.isArray(log?.[k])) return log[k].length;
  }
  return null;
}

export function getProposedCount(log) {
  const type = lower(log?.type);
  const explicit = readCountFromLog(
    log,
    ["proposed", "proposedCount", "totalProposed", "actions", "actionCount"],
    ["proposed", "proposedActions", "actions"],
  );
  if (explicit != null) return explicit;

  if (type === "brain_execute_summary" || type === "tick") {
    const executed = readCountFromLog(
      log,
      ["executedCount", "executed", "actionsExecuted"],
      ["executed", "executedActions"],
    );
    const skipped = readCountFromLog(
      log,
      ["skippedCount", "skipped", "skips", "skippedItemsCount"],
      ["skipped", "skippedItems"],
    );
    if (executed != null && skipped != null) return executed + skipped;
  }
  return null;
}

export function getExecutedCount(log) {
  const type = lower(log?.type);
  // brain_dedup "kept" means survived dedup, not actually executed.
  if (type === "brain_dedup") return null;
  const direct = readCountFromLog(
    log,
    ["executedCount", "executed", "actionsExecuted"],
    ["executed", "executedActions"],
  );
  if (direct != null) return direct;
  if (isExecutedType(type)) return 1;
  if (isFailureType(type) || isSkippedType(type)) return 0;
  return null;
}

export function getSkippedCount(log) {
  const type = lower(log?.type);
  const direct = readCountFromLog(
    log,
    ["skippedCount", "skipped", "skips", "skippedItemsCount"],
    ["skipped", "skippedItems", "skippedPreview"],
  );
  if (direct != null) return direct;

  if (type === "brain_dedup") {
    const proposed = readCountFromLog(
      log,
      ["proposed", "proposedCount", "totalProposed", "actions"],
      ["proposed", "proposedActions", "actions"],
    );
    const kept = readCountFromLog(log, ["kept", "keptCount"], ["kept", "keptActions"]);
    if (proposed != null && kept != null) return Math.max(0, proposed - kept);
  }

  const proposed = getProposedCount(log);
  const executed = getExecutedCount(log);
  if (proposed != null && executed != null) return Math.max(0, proposed - executed);

  if (isSkippedType(type)) return 1;
  return 0;
}

/* ------------------------------------------------------------ pagination */

export function normalizeLogPagination(rows, fallbackPage = 1, fallbackPageSize = 500) {
  const safePageSize = Math.max(
    1,
    Number(rows?.pagination?.pageSize || fallbackPageSize || 500),
  );
  const logCount = Array.isArray(rows?.logs) ? rows.logs.length : 0;
  const totalCountRaw = Number(rows?.pagination?.totalCount);
  const totalCount =
    Number.isFinite(totalCountRaw) && totalCountRaw >= 0 ? totalCountRaw : logCount;
  const totalPagesRaw = Number(rows?.pagination?.totalPages);
  const totalPages =
    Number.isFinite(totalPagesRaw) && totalPagesRaw > 0
      ? Math.floor(totalPagesRaw)
      : Math.max(1, Math.ceil(totalCount / safePageSize));
  const requestedPage = Number(rows?.pagination?.page || fallbackPage || 1);
  const page = Math.min(
    totalPages,
    Math.max(1, Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1),
  );
  const startIndex =
    totalCount === 0
      ? 0
      : Number(rows?.pagination?.startIndex || (page - 1) * safePageSize + 1);
  const endIndex =
    totalCount === 0
      ? 0
      : Number(
          rows?.pagination?.endIndex ||
            Math.min(startIndex + Math.max(logCount - 1, 0), totalCount),
        );

  return {
    page,
    pageSize: safePageSize,
    totalCount,
    totalPages,
    hasPrev: page > 1,
    hasNext: page < totalPages,
    startIndex,
    endIndex,
  };
}

/* ------------------------------------------- pending approval duplicates */

function normalizeApprovalText(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/\((?:requirements?-aligned|requirements? aligned)\)/g, "")
    .replace(/\b(?:requirements?-aligned|requirements? aligned)\b/g, "")
    .replace(/[^a-z0-9@._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function approvalLogDedupeKey(log) {
  const meta = logMeta(log);
  if (meta.dedupeKey) return String(meta.dedupeKey);
  const action = meta?.action && typeof meta.action === "object" ? meta.action : {};
  const type = normalizeApprovalText(meta.actionType || action.type || log?.type);
  const purpose = normalizeApprovalText(meta.purpose || "");
  const stable =
    action?.meta?.taskId ||
    action?.meta?.reportFp ||
    action?.meta?.reportWeekKey ||
    action?.meta?.reportPeriodKey ||
    "";
  const title = normalizeApprovalText(meta.title || action.title || action.topic || "");
  const target = normalizeApprovalText(
    meta.target || meta.toEmail || action.toEmail || action.to || action.ownerEmail || "",
  );
  return [purpose, type, stable || target, title].filter(Boolean).join("|");
}

/** Hide repeat pending approvals for the same action (newest first wins). */
export function dedupeVisibleApprovalLogs(logs = []) {
  const seenPendingApprovals = new Set();
  const out = [];
  for (const log of logs || []) {
    const type = lower(log?.type);
    const status = lower(logMeta(log)?.status || "pending");
    if (type === "approval_pending" && status === "pending") {
      const key = approvalLogDedupeKey(log);
      if (key && seenPendingApprovals.has(key)) continue;
      if (key) seenPendingApprovals.add(key);
    }
    out.push(log);
  }
  return out;
}

export function isActionableApproval(log) {
  return (
    lower(log?.type) === "approval_pending" &&
    lower(logMeta(log)?.status || "pending") === "pending"
  );
}

/* ----------------------------------------------------------------- names */

/** email -> display name, from the Team directory; falls back to the email. */
export function makeNameLookup(teamMembers = []) {
  const map = new Map();
  for (const m of Array.isArray(teamMembers) ? teamMembers : []) {
    const email = lower(m?.email);
    const name = str(m?.name || m?.displayName || m?.fullName);
    if (email && name && !map.has(email)) map.set(email, name);
  }
  return (value) => {
    const raw = str(value);
    if (!raw) return "";
    return map.get(raw.toLowerCase()) || raw;
  };
}

const identity = (v) => str(v);

function toList(value) {
  if (Array.isArray(value)) return value.map(str).filter(Boolean);
  return str(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** "Sam Okafor, Dana Whitfield" / "Sam Okafor, Dana Whitfield and 2 more". */
export function formatPeople(value, nameFor = identity, max = 3) {
  const names = Array.from(new Set(toList(value).map((e) => nameFor(e) || e)));
  if (!names.length) return "";
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} and ${names.length - max} more`;
}

/* ----------------------------------------------------------------- tools */

// Keys match TOOL_ICONS in components/ToolIcon.js.
const TOOL_KEYS = new Set([
  "outlook",
  "teams",
  "slack",
  "email",
  "google",
  "asana",
  "jira",
  "linear",
  "clickup",
  "notion",
  "monday",
  "planner",
  "github",
  "hubspot",
  "pipedrive",
  "bossdrop",
]);

export const TOOL_LABELS = {
  outlook: "Outlook",
  teams: "Teams",
  slack: "Slack",
  email: "Email",
  google: "Google Calendar",
  asana: "Asana",
  jira: "Jira",
  linear: "Linear",
  clickup: "ClickUp",
  notion: "Notion",
  monday: "monday.com",
  planner: "Planner",
  github: "GitHub",
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  bossdrop: "BossDrop",
};

const TOOL_ALIASES = {
  msteams: "teams",
  microsoft_teams: "teams",
  "microsoft teams": "teams",
  mail: "email",
  smtp: "email",
  gmail: "email",
  google_calendar: "google",
  gcal: "google",
  "monday.com": "monday",
  ms_planner: "planner",
  microsoft_planner: "planner",
};

/** A raw backend/channel value -> a known tool key, or null. */
export function normalizeToolKey(raw) {
  const k = lower(raw);
  if (!k) return null;
  const aliased = TOOL_ALIASES[k] || k;
  return TOOL_KEYS.has(aliased) ? aliased : null;
}

function uniqTools(list) {
  return Array.from(new Set(list.filter(Boolean)));
}

// meta.channels: escalations write { okSlack, okTeams, okEmail }; a plain
// { teams, slack, email } map is read the same way.
function toolsFromChannels(channels) {
  if (!channels || typeof channels !== "object") return [];
  const on = (a, b) => channels[a] === true || channels[b] === true;
  return uniqTools([
    on("okTeams", "teams") ? "teams" : null,
    on("okSlack", "slack") ? "slack" : null,
    on("okEmail", "email") ? "email" : null,
  ]);
}

const SYSTEM_TYPES = new Set([
  "pilot_baseline_held",
  "pilot_automated",
  "tick",
  "tick_skipped_business_hours",
  "tick_skipped_no_enabled_actions",
  "tick_error",
  "approval_gate",
  "pm_state",
  "retro_built",
]);

function isSystemType(t) {
  return SYSTEM_TYPES.has(t) || t.startsWith("brain_") || t.startsWith("memory_");
}

// Approval rows carry the queued action (meta.action) and, once run, the
// executor's summary (meta.result.executed[].backend).
function approvalTools(meta) {
  const action = meta?.action && typeof meta.action === "object" ? meta.action : {};
  const aType = lower(meta.actionType || action.type);
  if (aType === "reply_email" || aType === "send_meeting_notes_external") {
    return ["email"];
  }
  const executed = Array.isArray(meta?.result?.executed) ? meta.result.executed : [];
  const fromResult = uniqTools(executed.map((e) => normalizeToolKey(e?.backend)));
  if (fromResult.length) return fromResult;
  return uniqTools([
    normalizeToolKey(action.backend),
    normalizeToolKey(action.chatBackend),
  ]);
}

/** Tool keys a log row used, from its own fields only. [] when unknown. */
export function logTools(log) {
  const t = lower(log?.type);
  const meta = logMeta(log);

  if (t.startsWith("approval_") && t !== "approval_gate") return approvalTools(meta);
  if (isSystemType(t)) return ["bossdrop"];

  const pull = t.match(/^([a-z]+)_pull_error$/);
  if (pull) return uniqTools([normalizeToolKey(pull[1])]);
  if (t.startsWith("teams_")) return ["teams"];

  if (t.startsWith("email_reply_")) return ["email"];
  if (t === "meeting_notes_external_sent") return ["email"];
  // A custom automation's rows name the CRM when the sweep could not read it.
  if (t === "automation_sweep_error") {
    return uniqTools([normalizeToolKey(meta.crm)]);
  }
  if (t === "meeting_notes_teams_consent") return ["teams"];

  if (t === "meeting_scheduled" || t === "meeting_updated" || t === "meeting_cancelled") {
    const p = lower(meta.provider);
    if (p === "google") return ["google"];
    if (p === "microsoft" || p === "outlook") return ["outlook"];
    return [];
  }
  if (t.startsWith("meeting_notes_")) {
    // Google Meet has no mark in the hub, so only Teams meetings get one.
    const p = lower(meta.provider);
    return p === "microsoft" || p === "teams" ? ["teams"] : [];
  }

  const fromChannels = toolsFromChannels(meta.channels);
  if (fromChannels.length) return fromChannels;

  return uniqTools([
    normalizeToolKey(meta.backend),
    normalizeToolKey(meta.chatBackend),
    normalizeToolKey(meta.channel),
  ]);
}

function toolPhrase(tools) {
  return tools
    .filter((k) => k !== "bossdrop")
    .map((k) => TOOL_LABELS[k] || k)
    .join(" + ");
}

/** "Teams chat" / "Teams channel" / "Slack DM" for a chat delivery. */
function chatLabel(backend, delivery) {
  const b = normalizeToolKey(backend);
  if (b === "teams") return lower(delivery) === "channel" ? "Teams channel" : "Teams chat";
  if (b === "slack") return "Slack DM";
  return "";
}

/* ----------------------------------------------------------------- dates */

function asDate(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Fri, Sep 25" (year added when it is not this year). */
export function formatShortDate(ts, now = new Date()) {
  const d = asDate(ts);
  if (!d) return "";
  const opts = { weekday: "short", month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

export function timeAgo(ts, now = Date.now()) {
  const t = asDate(ts)?.getTime();
  if (!t) return "";
  const s = Math.max(1, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** { time: "7:00 AM", date: "Fri, Sep 25", full, ago } for the Time column. */
export function formatLogWhen(ts, now = new Date()) {
  const d = asDate(ts);
  if (!d) return { time: "", date: "Unknown date", full: "", ago: "" };
  return {
    time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    date: formatShortDate(d, now),
    full: d.toLocaleString(),
    ago: timeAgo(d, now.getTime()),
  };
}

function shortDateTime(ts) {
  const d = asDate(ts);
  if (!d) return "";
  return `${d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

/* --------------------------------------------------------------- summary */

const TASK_OUTCOME_PHRASES = {
  done: "marked done",
  completed: "marked done",
  complete: "marked done",
  blocked: "marked blocked",
  at_risk: "flagged at risk",
  in_progress: "marked in progress",
  started: "marked in progress",
};

const SKIP_REASON_PHRASES = {
  duplicate_fingerprint: "Already done recently",
  duplicate_guard: "Already done recently",
  meeting_no_slot_found: "No open slot found",
  "meeting_needs_2+_attendees": "Needs at least 2 attendees",
  // Changing a meeting BossDrop booked (backend brain/meetingChanges.js).
  meeting_not_found: "Not a meeting BossDrop booked",
  meeting_not_in_it: "Asked by someone not in the meeting",
  meeting_gone: "No longer on the calendar",
  meeting_calendar_unavailable: "Its calendar is not connected",
  meeting_change_failed: "The calendar refused the change",
  meeting_change_empty: "Nothing to change",
  meeting_agenda_already: "Already on the agenda",
  meeting_cancel_needs_approval: "Cancelling needs approval",
  meeting_change_needs_request: "Moves need someone to ask",
};

function isDuplicateReason(reason) {
  const r = lower(reason);
  return r === "duplicate_fingerprint" || r === "duplicate_guard" || r.includes("duplicate");
}

// Nouns for a queued action type ("a duplicate ping").
const ACTION_NOUNS = {
  create_task: "task",
  update_task: "task update",
  send_ping: "ping",
  ask_clarifying: "question",
  send_escalation: "escalation",
  schedule_meeting: "meeting",
  update_meeting: "meeting change",
  cancel_meeting: "meeting cancellation",
  send_weekly_report: "report",
  reply_email: "reply",
  send_meeting_notes_external: "meeting notes email",
};

function actionNoun(type) {
  return ACTION_NOUNS[lower(type)] || humanize(type) || "action";
}

function joinContext(items) {
  return items.map(str).filter(Boolean).join(" · ");
}

function cleanNotesTitle(title) {
  const t = str(title);
  const i = t.indexOf(": ");
  return i >= 0 ? t.slice(i + 2) : t;
}

// One queued or approved action, in "proposed" or "done" wording.
function describeAction(aType, action, meta, phase, nameFor, tools) {
  const done = phase === "done";
  const title = str(meta.title || action.title || action.topic);
  const personEmail =
    action.toEmail || action.to || action.ownerEmail || meta.toEmail || meta.target;
  const person = formatPeople(personEmail, nameFor, 2);
  const toolLabel = toolPhrase(tools);
  const due = action.dueISO ? `due ${formatShortDate(action.dueISO)}` : "";

  switch (aType) {
    case "custom_automation_run":
      return {
        lead: done ? "Ran the automation" : "Wants to run the automation",
        entity: title || "Custom automation",
        context: joinContext([shortText(action.body, 140)]),
      };
    case "send_message":
      return {
        lead: done ? "Sent a message" : "Drafted a message",
        entity: title || "A note",
        context: person ? `to ${person}` : "",
      };
    case "create_project":
      return {
        lead: done ? "Created project" : "Drafted project",
        entity: title || "Untitled project",
        context: toolLabel,
      };
    case "create_task":
      return {
        lead: done ? "Created task" : "Drafted task",
        entity: title || "Untitled task",
        context: joinContext([toolLabel, person ? `for ${person}` : "", due]),
      };
    case "update_task":
      return {
        lead: done ? "Updated" : "Drafted an update to",
        entity: title || (action?.meta?.taskId ? `task ${action.meta.taskId}` : "a task"),
        context: joinContext([toolLabel, person ? `for ${person}` : ""]),
      };
    // The bold name is a person: a link goes on the title in the context line.
    case "send_ping":
      return {
        lead: done ? "Pinged" : "Drafted a ping to",
        entity: person || "a teammate",
        context: joinContext([toolLabel, title]),
        hrefLabel: title,
      };
    case "ask_clarifying":
      return {
        lead: done ? "Asked" : "Drafted a question for",
        entity: person || "a teammate",
        context: joinContext([toolLabel, title]),
        hrefLabel: title,
      };
    case "send_escalation":
      return {
        lead: done ? "Escalated to" : "Drafted an escalation to",
        entity: person || "a manager",
        context: joinContext([toolLabel, title]),
        hrefLabel: title,
      };
    case "send_weekly_report":
      return {
        lead: done ? "Sent the" : "Drafted the",
        entity: "executive report",
        context: joinContext([toolLabel, person ? `To ${person}` : ""]),
      };
    case "schedule_meeting": {
      const attendees = Array.isArray(action.attendees) ? action.attendees.length : 0;
      return {
        lead: done ? "Scheduled" : "Drafted meeting",
        entity: title || "a meeting",
        context: joinContext([toolLabel, attendees ? plural(attendees, "attendee") : ""]),
      };
    }
    case "update_meeting": {
      const am = action.meta && typeof action.meta === "object" ? action.meta : {};
      const items = Array.isArray(am.agendaItems) ? am.agendaItems.length : 0;
      return {
        lead: done ? "Updated meeting" : "Drafted a change to",
        entity: title || "a meeting",
        context: joinContext([
          toolLabel,
          am.startISO ? "New time" : "",
          am.newTitle ? `Rename to ${str(am.newTitle)}` : "",
          items ? `${plural(items, "agenda item")} to add` : "",
        ]),
      };
    }
    case "cancel_meeting":
      return {
        lead: done ? "Cancelled" : "Drafted cancelling",
        entity: title || "a meeting",
        context: joinContext([toolLabel, "The invitees are told"]),
      };
    case "reply_email":
      return {
        lead: done ? "Replied to" : "Drafted a reply to",
        entity: person || "a sender",
        context: joinContext([toolLabel, title]),
        hrefLabel: title,
      };
    case "send_meeting_notes_external": {
      const n = toList(action.toEmail).length;
      return {
        lead: done ? "Sent meeting notes to" : "Drafted meeting notes for",
        entity: n ? plural(n, "external guest") : "external guests",
        context: joinContext([toolLabel, cleanNotesTitle(title)]),
      };
    }
    default:
      return {
        lead: done ? "Ran" : "Drafted",
        entity: title || actionNoun(aType),
        context: joinContext([toolLabel, person ? `for ${person}` : ""]),
      };
  }
}

function approvalSummary(t, meta, nameFor, tools) {
  const action = meta?.action && typeof meta.action === "object" ? meta.action : {};
  const aType = lower(meta.actionType || action.type);
  if (t === "approval_gate") {
    const n = Number(meta.queuedOrSkipped) || 0;
    return {
      lead: "Sent",
      entity: plural(n, "action"),
      tail: "to the approval queue",
      context: lower(meta.automationMode) === "auto" ? "Auto mode" : "Manual mode",
    };
  }
  const phase = t === "approval_executed" ? "done" : "proposed";
  const described = describeAction(aType, action, meta, phase, nameFor, tools);
  // Approved, but the pilot's watch-only baseline held it: the server's one
  // sentence says until when, and that it will be proposed again.
  if (t === "approval_held") {
    return {
      ...described,
      context: shortText(meta.note, 180) || "Held for the pilot baseline",
    };
  }
  // Approved, but what it was about moved on while it waited (the task was
  // done, the block cleared): not sent, and the server's sentence says why.
  if (t === "approval_not_needed") {
    return {
      ...described,
      context: shortText(meta.note, 180) || "It no longer applied when it was approved",
    };
  }
  return described;
}

// One row per run in which the pilot's watch-only baseline held something
// (backend automationWorker.js): counts, never one row per action.
function pilotHeldSummary(meta) {
  const followups =
    (Number(meta.meetingFollowups) || 0) +
    (Number(meta.staleEscalationFollowups) || 0) +
    (Number(meta.integrationFollowups) || 0);
  const held = (Number(meta.actionsHeld) || 0) + followups;
  const automatic = lower(meta.run) !== "direct";
  const types = (Array.isArray(meta.typesHeld) ? meta.typesHeld : [])
    .map((x) => actionNoun(x))
    .filter(Boolean);
  return {
    lead: "Held",
    entity: held
      ? plural(held, "action")
      : automatic
        ? "the automatic run"
        : "an action",
    tail: "for the pilot baseline",
    context: joinContext([
      Number(meta.day) > 0 ? `Day ${Number(meta.day)}` : "",
      automatic ? "Automatic run" : "Direct request",
      Array.from(new Set(types)).join(", "),
      Number(meta.meetingFollowups)
        ? plural(Number(meta.meetingFollowups), "meeting follow-up")
        : "",
      Number(meta.staleEscalationFollowups)
        ? plural(Number(meta.staleEscalationFollowups), "stale-task escalation")
        : "",
      Number(meta.integrationFollowups)
        ? plural(Number(meta.integrationFollowups), "tool follow-up")
        : "",
    ]),
  };
}

/**
 * The Summary cell for one log: a sentence with one bold entity, plus a
 * short context line shown after the tool icon.
 *
 * @returns {{ lead: string, entity: string, tail: string, context: string,
 *             tools: string[] }}
 */
export function formatLogSummary(log, { nameFor = identity } = {}) {
  const t = lower(log?.type);
  const m = logMeta(log);
  const tools = logTools(log);
  const toolLabel = toolPhrase(tools);
  const who = (v, fallback) => formatPeople(v, nameFor, 3) || fallback;
  // With nothing else to say, the context line names the tool itself, so an
  // icon never sits there alone.
  const out = (s) => ({
    lead: str(s.lead),
    entity: str(s.entity),
    tail: str(s.tail),
    context: str(s.context) || tools.map((k) => TOOL_LABELS[k] || k).join(" + "),
    tools,
    // The task or meeting the row is about, when the log has its link (Oct
    // 28 2026): the row's bold name links to it, or, on a row whose bold name
    // is a person (a ping, a question, an escalation), `hrefLabel`, the
    // task's name in the context line.
    href: logLink(log),
    hrefLabel: str(s.hrefLabel),
    // Why BossDrop did it, or is holding off, in the plan's own words. The server
    // sends this to admins only (backend utils/auditLogView.js).
    why: shortText(
      str(m.why || (t.startsWith("approval_") ? m.reason : "")).split(/\s*\|\s*critic:/i)[0],
      400,
    ),
  });

  if (t.startsWith("approval_")) return out(approvalSummary(t, m, nameFor, tools));
  if (t === "pilot_baseline_held") return out(pilotHeldSummary(m));
  if (t === "holding_off") {
    const waiting = who(m.waitingOn, "");
    return out({
      lead: "Holding off on",
      entity: m.about || waiting || "a follow-up",
      context: joinContext([
        waiting ? `Waiting on ${waiting}` : "",
        m.to ? `Would go to ${who(m.to, "")}` : "",
      ]),
    });
  }
  if (t === "pilot_automated") {
    return out({
      lead: "Switched",
      entity: "to Automated",
      tail: "for the pilot",
      context: joinContext([
        Number(m.day) > 0 ? `Day ${Number(m.day)}` : "",
        lower(m.from) === "manual" ? "Was Manual" : "",
        Array.isArray(m.approvalsCleared) && m.approvalsCleared.length
          ? plural(m.approvalsCleared.length, "approval setting") + " cleared"
          : "",
      ]),
    });
  }

  switch (t) {
    case "report_sent":
      return out({
        lead: "Sent the",
        entity: "executive report",
        context: toolLabel
          ? `${toolLabel} to ${who(m.to || m.recipient || m.email, "recipients")}`
          : `To ${who(m.to || m.recipient || m.email, "recipients")}`,
      });
    case "report_duplicate_race":
      return out({
        lead: "Possible second send of the",
        entity: "executive report",
        context: joinContext([who(m.to, "") ? `To ${who(m.to, "")}` : ""]),
      });

    case "project_created":
      return out({
        lead: "Created project",
        entity: m.title || "Untitled project",
        context: joinContext([
          toolLabel,
          m.kind === "group" ? "a group on a board" : m.kind === "board" ? "a new board" : "",
          m.followed === false ? "past the board limit, so not followed" : "",
        ]),
      });
    case "project_skipped":
      return out({
        lead: "Skipped project",
        entity: m.title || "a project",
        context:
          lower(m.reason) === "project_not_supported"
            ? "BossDrop can only create projects in monday.com so far"
            : capitalize(humanize(m.reason)),
      });
    case "automation_created":
      return out({
        lead: "Created a custom automation",
        entity: "",
        context: m.by ? `by ${who(m.by, "")}` : "",
      });
    case "automation_built":
      return out({
        lead: "Read the prompt for",
        entity: m.automation || "a custom automation",
        context: joinContext([
          Number(m.steps) ? plural(Number(m.steps), "step") : "",
          Number(m.questions) ? `${plural(Number(m.questions), "question")} for you` : "",
          Number(m.unsupported) ? `${Number(m.unsupported)} it cannot do` : "",
        ]),
      });
    case "automation_switched":
      return out({
        lead: lower(m.turned) === "on" ? "Turned on" : "Turned off",
        entity: m.automation || "a custom automation",
        context: m.by ? `by ${who(m.by, "")}` : "",
      });
    case "automation_prompt_changed":
      return out({
        lead: "Changed the prompt for",
        entity: m.automation || "a custom automation",
        context: m.by ? `by ${who(m.by, "")}` : "",
      });
    case "automation_trigger_changed":
      return out({
        lead: "Changed when this runs:",
        entity: m.automation || "a custom automation",
        context: m.by ? `by ${who(m.by, "")}` : "",
      });
    case "automation_run_recovered":
      return out({
        lead: `Picked up ${m.automation || "a custom automation"} for`,
        entity: m.title || "this run",
        context: Number(m.recovered) ? `${plural(Number(m.recovered), "action")} done before a restart` : "",
      });
    case "automation_deleted":
      return out({
        lead: "Deleted the automation",
        entity: m.automation || "a custom automation",
        context: m.by ? `by ${who(m.by, "")}` : "",
      });
    case "automation_run_started":
      return out({
        lead: `Started ${m.automation || "a custom automation"} for`,
        entity: m.title || "this run",
        context: joinContext([
          ({
            deal_won: "the deal was won",
            deal_stage: "the deal reached its stage",
            deal_lost: "the deal was lost",
            deal_created: "a new deal",
            schedule: "on its schedule",
            manual: "run by hand",
          })[lower(m.event)] || "",
          lower(m.status) === "awaiting_approval" ? "waiting for approval" : "",
        ]),
      });
    case "automation_run_held":
      return out({
        lead: `Held ${m.automation || "a custom automation"} for`,
        entity: m.title || "this run",
        context: "The pilot is in its watch-only days. Run it from its card under Custom automations.",
      });
    case "automation_run_checked":
      return out({
        lead: `Checked ${m.automation || "a custom automation"} for`,
        entity: m.title || "this run",
        context: m.summary || "",
      });
    case "automation_run_done":
      return out({
        lead: `Finished ${m.automation || "a custom automation"} for`,
        entity: m.title || "this run",
        context: Number(m.did) ? plural(Number(m.did), "action") : "",
      });
    case "automation_run_needs_attention":
      return out({
        lead: `${m.automation || "A custom automation"} needs you for`,
        entity: m.title || "this run",
        context:
          lower(m.reason) === "check_unavailable"
            ? "BossDrop could not check the result"
            : lower(m.reason) === "too_many_passes"
              ? "It kept stopping part way, so BossDrop has stopped trying"
            : Array.isArray(m.needs) && m.needs.length
              ? shortText(m.needs[0], 140)
              : Array.isArray(m.failed) && m.failed.length
                ? `${plural(m.failed.length, "step")} did not go through`
                : "",
      });
    case "automation_run_denied":
      return out({
        lead: `Denied ${m.automation || "a custom automation"} for`,
        entity: m.title || "this run",
        context: "",
      });
    case "automation_run_notice_sent":
      return out({
        lead:
          lower(m.kind) === "attention"
            ? `Told the team what ${m.automation || "the automation"} needs for`
            : lower(m.kind) === "stopped"
              ? `Told the team ${m.automation || "the automation"} has stopped waiting for`
              : `Told the team ${m.automation || "the automation"} finished for`,
        entity: m.title || "this run",
        context: who(m.to, ""),
      });
    case "automation_sweep_error":
      return out({
        lead: "Could not check for deals in",
        entity: TOOL_LABELS[normalizeToolKey(m.crm)] || "the CRM",
        context: friendlyReason(m.err),
      });
    case "message_sent":
      return out({
        lead: "Sent a message",
        entity: m.title || "A note",
        context: joinContext([m.to ? `to ${who(m.to, "")}` : "", lower(m.via) === "email" ? "by email" : TOOL_LABELS[normalizeToolKey(m.via)] ? `in ${TOOL_LABELS[normalizeToolKey(m.via)]}` : ""]),
      });
    case "task_created":
      return out({
        lead: "Created task",
        entity: m.title || m.name || "Untitled task",
        context: joinContext([
          toolLabel,
          m.assignee || m.owner || m.ownerEmail
            ? `for ${who(m.assignee || m.owner || m.ownerEmail, "")}`
            : "",
          m.priority ? `${lower(m.priority)} priority` : "",
          Array.isArray(m.warnings) && m.warnings.length
            ? plural(m.warnings.length, "warning")
            : "",
        ]),
      });
    case "task_updated":
      return out({
        lead: "Updated",
        entity: m.title || m.taskTitle || (m.id ? `task ${m.id}` : "a task"),
        context: joinContext([
          toolLabel,
          TASK_OUTCOME_PHRASES[lower(m.taskOutcome)] ||
            (m.taskOutcome ? `status ${humanize(m.taskOutcome)}` : ""),
          m.ownerEmail ? `owner ${who(m.ownerEmail, "")}` : "",
          m.priority ? `${lower(m.priority)} priority` : "",
        ]),
      });
    case "dod_blocked_completion":
      return out({
        lead: "Kept",
        entity: m.taskId ? `task ${m.taskId}` : "a task",
        tail: "open",
        context: Array.isArray(m.failed) && m.failed.length
          ? `Definition of done not met: ${m.failed.slice(0, 3).join(", ")}`
          : "Definition of done not met",
      });

    case "ping_sent":
      // A done notice (backend pm/doneNotices.js): news, not a status check.
      if (m.note === "done_notice") {
        return out({
          lead: "Told",
          entity: who(m.to, "a teammate"),
          tail: "the work is done",
          context: joinContext([chatLabel(m.chatBackend, m.delivery), m.taskTitle]),
          hrefLabel: m.taskTitle,
        });
      }
      return out({
        lead: "Asked",
        entity: who(m.to || m.owner || m.ownerEmail, "a teammate"),
        tail: "for a status update",
        context: joinContext([chatLabel(m.chatBackend, m.delivery), m.taskTitle || m.topic]),
        hrefLabel: m.taskTitle,
      });
    case "clarify_sent":
      return out({
        lead: "Asked",
        entity: who(m.to, "a teammate"),
        tail: "a clarifying question",
        context: joinContext([chatLabel(m.chatBackend, m.delivery), m.taskTitle || m.topic]),
        hrefLabel: m.taskTitle,
      });
    case "ping_warning":
    case "clarify_warning":
      return out({
        lead: "Could not reach",
        entity: who(m.to, "a teammate"),
        context: joinContext([
          chatLabel(m.chatBackend),
          capitalize(humanize(m.warningCode)) || friendlyReason(m.reason),
        ]),
      });
    case "ping_failed":
      return out({
        lead: "Could not ping",
        entity: who(m.to, "a teammate"),
        context: joinContext([chatLabel(m.chatBackend), friendlyReason(m.reason || m.err)]),
      });

    case "escalation_sent": {
      const about = m.about || m.reason || m.taskTitle || m.topic || m.riskTitle;
      return out({
        lead: "Escalated to",
        entity: who(m.to || m.owner || m.ownerEmail, "a manager"),
        context: joinContext([toolLabel, about]),
        hrefLabel: about,
      });
    }
    case "escalation_failed":
      return out({
        lead: "Could not escalate to",
        entity: who(m.to, "a manager"),
        context: joinContext([m.about]),
      });
    case "escalation_skipped":
      return m.to
        ? out({
            lead: "Skipped an escalation to",
            entity: who(m.to, ""),
            context: joinContext([m.about, SKIP_REASON_PHRASES[lower(m.reason)]]),
          })
        : out({
            lead: "Skipped an escalation about",
            entity: m.about || "an issue",
            context: SKIP_REASON_PHRASES[lower(m.reason)] || capitalize(humanize(m.reason)),
          });
    case "escalation_deferred_bh":
      return out({
        lead: "Held an escalation to",
        entity: who(m.to, "a manager"),
        tail: "until business hours",
        context: joinContext([m.about, m.tz]),
      });
    case "escalation_rerouted":
      return out({
        lead: "Rerouted an escalation to",
        entity: who(m.to, "a manager"),
        context: m.from ? `Was going to ${who(m.from, "")} · org chart` : "Org chart",
      });

    case "meeting_scheduled": {
      const n = Array.isArray(m.attendees) ? m.attendees.length : 0;
      return out({
        lead: "Scheduled",
        entity: m.topic || m.title || "a meeting",
        context: joinContext([
          toolLabel,
          n ? plural(n, "attendee") : "",
          shortDateTime(m.startIso || m.start),
        ]),
      });
    }
    case "meeting_skipped":
      return out({
        lead: "Skipped meeting",
        entity: m.topic || m.title || "a meeting",
        context: SKIP_REASON_PHRASES[lower(m.reason)] || capitalize(humanize(m.reason)),
      });
    case "meeting_updated": {
      const items = Array.isArray(m.agendaItems) ? m.agendaItems.length : 0;
      return out({
        lead: "Updated meeting",
        entity: m.title || m.topic || "a meeting",
        context: joinContext([
          toolLabel,
          m.startIso ? `Moved to ${shortDateTime(m.startIso)}` : "",
          m.title && m.topic && m.title !== m.topic ? `Was ${m.topic}` : "",
          items ? `${plural(items, "agenda item")} added` : "",
        ]),
      });
    }
    case "meeting_cancelled":
      return out({
        lead: "Cancelled meeting",
        entity: m.topic || m.title || "a meeting",
        context: m.gone
          ? "Already off the calendar"
          : joinContext([toolLabel, shortDateTime(m.startIso), "Invitees told"]),
      });
    case "meeting_change_skipped":
      return out({
        lead: "Did not change meeting",
        entity: m.topic || "a meeting",
        context: SKIP_REASON_PHRASES[lower(m.reason)] || capitalize(humanize(m.reason)),
      });

    case "email_reply_sent":
      return out({
        lead: "Replied to",
        entity: who(m.to, "a sender"),
        context: joinContext([toolLabel, m.subject]),
      });
    case "email_reply_failed":
      return out({
        lead: "Could not reply to",
        entity: who(m.to, "a sender"),
        context: joinContext([toolLabel, m.subject]),
      });
    case "email_reply_cap_reached":
      return out({
        lead: "Held a reply to",
        entity: who(m.to, "a sender"),
        context: m.cap ? `Daily reply cap of ${m.cap} reached` : "Daily reply cap reached",
      });

    case "action_error":
      return out({
        lead: "Could not run",
        entity: m.title || actionNoun(m.type),
        context: joinContext([m.title ? capitalize(actionNoun(m.type)) : "", shortText(m.err, 70)]),
      });

    case "brain_plan":
      return out({
        lead: "Planned",
        entity: plural(Number(m.actions) || 0, "action"),
        context: joinContext([
          m.reportDue ? "Report due" : "",
          Number(m.statusPingSignals) ? plural(m.statusPingSignals, "status signal") : "",
          Number(m.integrationFollowups)
            ? plural(m.integrationFollowups, "integration follow-up")
            : "",
        ]),
      });
    case "brain_dedup": {
      const proposed = Number(m.proposed) || 0;
      const kept = Number(m.kept) || 0;
      const skipped = Math.max(0, proposed - kept);
      const preview = Array.isArray(m.skippedPreview) ? m.skippedPreview : [];
      const bypass =
        m.mode === "approval_override"
          ? "Approved action, duplicate check bypassed"
          : m.mode === "explicit_command_dedup_bypass"
            ? "Direct command, duplicate check bypassed"
            : "";
      if (!skipped) {
        return out({
          lead: "Checked",
          entity: plural(proposed, "proposed action"),
          tail: "for duplicates",
          context: bypass,
        });
      }
      if (skipped === 1 && preview[0]?.title) {
        return out({
          lead: `Skipped a duplicate ${actionNoun(preview[0].type)}:`,
          entity: preview[0].title,
          context: `Kept ${kept} of ${proposed} proposed`,
        });
      }
      return out({
        lead: "Skipped",
        entity: plural(skipped, "duplicate action"),
        context: `Kept ${kept} of ${proposed} proposed`,
      });
    }
    case "brain_execute_summary": {
      const executed = Number(m.executedCount) || 0;
      const skipped = Number(m.skippedCount) || 0;
      const queued = Number(m.approvalsQueuedOrSkipped) || 0;
      return out({
        lead: "Ran",
        entity: plural(executed, "action"),
        tail: skipped ? `and skipped ${skipped}` : "",
        context: m.approvedBy
          ? `Approved by ${who(m.approvedBy, "")}`
          : queued
            ? `${queued} sent for approval`
            : "",
      });
    }
    case "tick":
      return out({
        lead: "Finished an automation run:",
        entity: `${Number(m.executed) || 0} executed, ${Number(m.skipped) || 0} skipped`,
      });
    case "tick_skipped_business_hours": {
      const w = m.window || {};
      const days = Array.isArray(w.days) && w.days.length ? `${w.days.join(", ")} ` : "";
      const hours = w.start && w.end ? `${w.start} to ${w.end}` : "business hours";
      return out({
        lead: "Paused scheduled polling outside",
        entity: `${days}${hours}${w.tz ? ` ${w.tz}` : ""}`,
        context: `${m.resumesAt ? `Resumes ${m.resumesAt}. ` : ""}Inbound email, chat commands and approvals still run`,
      });
    }
    case "tick_skipped_no_enabled_actions":
      return out({ lead: "Skipped a run:", entity: "no automations enabled" });
    case "tick_error":
      return out({
        lead: "Automation run",
        entity: "failed",
        context: shortText(m.e || m.error || m.reason, 90),
      });

    case "integration_signals_error":
    case "integration_escalation_error":
      return out({
        lead: "Integration check",
        entity: "failed",
        context: shortText(m.e || m.error, 90),
      });

    case "teams_chat_pruned_not_found":
      return out({ lead: "Stopped reading a", entity: "Teams chat", tail: "that no longer exists" });
    case "teams_chat_read_skipped":
      return out({
        lead: "Skipped reading a",
        entity: "Teams chat",
        context: shortText(m.reason || m.e, 80),
      });
    case "teams_binding_conflict":
      return out({
        lead: "Microsoft tenant claimed by",
        entity: plural(Number(m.claimants) || 2, "workspace"),
        context: "Teams stays unbound until one workspace owns the tenant",
      });

    case "pm_state":
      return out({
        lead: "Updated",
        entity: "project health",
        context: joinContext([
          `${Number(m.registersOpened) || 0} opened, ${Number(m.registersClosed) || 0} closed`,
          m.spi != null ? `SPI ${Number(m.spi).toFixed(2)}` : "",
        ]),
      });
    case "memory_indexed":
      return out({
        lead: "Indexed",
        entity: plural(Number(m.indexed) || 0, "item"),
        tail: "into memory",
      });
    case "memory_consolidated":
      return out({ lead: "Consolidated", entity: "memory" });
    case "memory_rollup_built":
      return out({
        lead: "Built the",
        entity: `${m.period === "week" ? "weekly" : humanize(m.period) || ""} memory rollup`.trim(),
        context: str(m.periodKey),
      });
    case "retro_built":
      return out({ lead: "Built the", entity: "retro" });
    case "memory_cleared":
      return out({
        lead: "Cleared",
        entity: plural(Number(m.deleted) || 0, "memory item"),
        context: m.scope ? `Scope: ${humanize(m.scope)}` : "",
      });

    case "meeting_notes_scheduled":
      return out({
        lead: "Set up meeting notes for",
        entity: m.meetingId ? `meeting ${m.meetingId}` : "a meeting",
        context: joinContext([
          lower(m.provider) === "google" ? "Google Meet" : "",
          Number(m.noticesSent) ? `${plural(m.noticesSent, "guest")} notified` : "",
        ]),
      });
    case "meeting_notes_processed":
      return out({
        lead: "Wrote",
        entity: "meeting notes",
        context: joinContext([
          lower(m.provider) === "google" ? "Google Meet" : "",
          plural(Number(m.actionItems) || 0, "action item"),
          Number(m.tasksCreated) ? `${plural(m.tasksCreated, "task")} created` : "",
          Number(m.pendingApproval) ? `${m.pendingApproval} waiting for approval` : "",
        ]),
      });
    case "meeting_notes_no_transcript":
      return out({
        lead: "No transcript for",
        entity: m.meetingId ? `meeting ${m.meetingId}` : "a meeting",
        context: capitalize(humanize(m.reason)),
      });
    case "meeting_notes_failed":
      return out({
        lead: "Meeting notes",
        entity: "failed",
        context: joinContext([
          capitalize(humanize(m.reason)),
          m.attempts ? plural(m.attempts, "attempt") : "",
        ]),
      });
    case "meeting_notes_stopped":
      return out({
        lead: "Stopped meeting notes for",
        entity: m.meetingId ? `meeting ${m.meetingId}` : "a meeting",
        context: capitalize(humanize(m.reason)),
      });
    case "meeting_notes_external_sent":
      return out({
        lead: "Sent meeting notes to",
        entity: plural(Number(m.sent) || 0, "external guest"),
        context: joinContext([toolLabel, Number(m.failed) ? `${m.failed} failed` : ""]),
      });
    case "meeting_notes_teams_consent":
      return out({ lead: "Granted", entity: "Teams transcript access" });
    case "meeting_notes_tenant_mismatch":
      return out({
        lead: "Skipped notes for",
        entity: m.meetingId ? `meeting ${m.meetingId}` : "a meeting",
        context: capitalize(humanize(m.reason)) || "Meeting is outside this workspace",
      });
    case "meeting_notes_opted_out":
      return out({
        lead: "A guest opted out of",
        entity: "meeting notes",
        context: m.transcriptDeleted ? "Transcript deleted" : "",
      });
    default:
      break;
  }

  const pull = t.match(/^([a-z]+)_pull_error$/);
  if (pull) {
    return out({
      lead: "Could not read",
      entity: TOOL_LABELS[normalizeToolKey(pull[1])] || capitalize(pull[1]),
      context: shortText(m.e || m.error || m.reason, 90),
    });
  }

  // Unknown type: its name, plus the first few plain fields of meta.
  const scalars = Object.entries(m)
    .filter(([k, v]) => k !== "fp" && v != null && v !== "" && typeof v !== "object")
    .slice(0, 3)
    .map(([k, v]) => `${humanize(k)}: ${shortText(v, 40)}`);
  return out({
    lead: "",
    entity: capitalize(humanize(t)) || "Unknown event",
    context: joinContext([toolLabel, ...scalars]),
  });
}

// A well-formed https link, else "". Logged links come from task tools and
// calendars; nothing else may reach an href.
function safeHttps(value) {
  const s = typeof value === "string" ? value.trim() : "";
  if (!s || s.length > 2000 || /[\s<>"'`\\]/.test(s)) return "";
  try {
    const u = new URL(s);
    return u.protocol === "https:" && u.hostname.includes(".") && !u.username && !u.password ? u.href : "";
  } catch {
    return "";
  }
}

/**
 * The link a log row is about, when the log carries one: the task a ping,
 * question or escalation named or a task change made (taskUrl), the meeting
 * it booked (link, joinUrl), or the first thing an approval ran. "" if none.
 */
export function logLink(log) {
  const m = logMeta(log);
  const executed = Array.isArray(m?.result?.executed) ? m.result.executed : [];
  const first = executed[0] || {};
  // An approval row: the task it is about (stamped when queued), then what it
  // ran (a created task's own link, older rows included, or the meeting).
  const created = first.created && typeof first.created === "object" ? first.created : {};
  const candidates = [
    m.taskUrl,
    m.projectUrl,
    m.url,
    m.dealUrl,
    m.link,
    m.joinUrl,
    first.url,
    created.url,
    created.permalink_url,
    created.link,
    first.joinUrl,
    first.link,
  ];
  for (const c of candidates) {
    const href = safeHttps(c);
    if (href) return href;
  }
  return "";
}

/** The summary as one plain line (search, titles, tests). */
export function summaryText(summary) {
  return [summary?.lead, summary?.entity, summary?.tail].filter(Boolean).join(" ");
}

/* --------------------------------------------------------------- outcome */

/**
 * The Outcome pill for one log.
 *
 * tone: ok | approved | queued | info | warn | fail | skip | denied | neutral
 * icon: dot | check | clock | alert | x | skip
 * by:   the email of whoever approved, denied or cleared it, when logged.
 */
export function logOutcome(log) {
  const t = lower(log?.type);
  const m = logMeta(log);
  const res = (tone, icon, label, by = null) => ({ tone, icon, label, by: str(by) || null });

  switch (t) {
    case "approval_pending": {
      const status = lower(m.status || "pending");
      if (status === "pending") return res("queued", "clock", "Queued for approval");
      if (status === "held") return res("neutral", "clock", "Held for pilot baseline");
      return res("neutral", "dot", capitalize(humanize(m.status)));
    }
    case "approval_executing":
      return res("info", "clock", "Approved, running", m.approvedBy);
    case "approval_executed":
      return res("approved", "check", "Approved", m.approvedBy);
    case "approval_warning":
      return res("warn", "alert", "Approved, not delivered", m.approvedBy);
    case "approval_duplicate":
      return res("skip", "skip", "Already handled", m.approvedBy);
    case "approval_denied":
      return res("denied", "x", "Denied", m.deniedBy);
    case "approval_failed":
      return res("fail", "x", "Failed", m.approvedBy);
    // The pilot's watch-only baseline held it: neither a failure nor a skip.
    case "approval_held":
      return res("neutral", "clock", "Held for pilot baseline", m.approvedBy);
    // Approved after the work moved on (backend brain/approvalStaleness.js).
    case "approval_not_needed":
      return res("skip", "skip", "No longer needed", m.approvedBy);
    case "pilot_baseline_held":
      return res("neutral", "clock", "Held for pilot baseline");
    case "pilot_automated":
      return res("neutral", "dot", "Automated");
    case "holding_off":
      return res("neutral", "clock", "Waiting");
    case "approval_gate":
      return res("queued", "clock", "Queued");

    case "project_created":
      return res("ok", "dot", "Created");
    case "project_skipped":
      return res("warn", "alert", "Skipped");
    case "automation_created":
      return res("info", "dot", "Created");
    case "automation_built":
      return res("ok", "dot", "Read");
    case "automation_switched":
      return res("info", "dot", lower(m.turned) === "on" ? "On" : "Off");
    case "automation_deleted":
      return res("neutral", "dot", "Deleted");
    case "automation_prompt_changed":
    case "automation_trigger_changed":
      return res("info", "dot", "Changed");
    case "automation_run_recovered":
      return res("info", "dot", "Picked up");
    case "automation_run_started":
      return lower(m.status) === "awaiting_approval"
        ? res("queued", "clock", "Waiting for approval")
        : res("info", "dot", "Started");
    case "automation_run_held":
      return res("neutral", "clock", "Held for pilot baseline");
    case "automation_run_checked":
      return lower(m.verdict) === "complete"
        ? res("ok", "dot", "All done")
        : lower(m.verdict) === "needs_person"
          ? res("warn", "alert", "Needs you")
          : lower(m.verdict) === "waiting"
            ? res("neutral", "clock", "Waiting")
            : lower(m.verdict) === "unchecked"
              ? res("warn", "alert", "Could not check")
              : res("info", "clock", "Trying again");
    case "automation_run_done":
      return res("ok", "dot", "Done");
    case "automation_run_needs_attention":
      return res("warn", "alert", "Needs you");
    case "automation_run_denied":
      return res("neutral", "dot", "Denied");
    case "automation_run_notice_sent":
      return res("ok", "dot", "Sent");
    case "message_sent":
      return res("ok", "dot", "Sent");
    case "task_created":
      return res("ok", "dot", "Created");
    case "task_updated":
      return res("ok", "dot", "Updated");
    case "meeting_scheduled":
      return res("ok", "dot", "Scheduled");
    case "meeting_updated":
      return res("ok", "dot", "Updated");
    case "meeting_cancelled":
      return res("ok", "dot", m.gone ? "Already gone" : "Cancelled");
    case "escalation_rerouted":
      return res("info", "dot", "Rerouted");
    case "escalation_deferred_bh":
      return res("warn", "clock", "Deferred");
    case "email_reply_cap_reached":
      return res("warn", "alert", "Daily cap reached");
    case "dod_blocked_completion":
      return res("warn", "alert", "Blocked");
    case "report_duplicate_race":
      return res("warn", "alert", "Possible duplicate");
    case "teams_binding_conflict":
    case "meeting_notes_tenant_mismatch":
      return res("warn", "alert", "Needs attention");
    case "meeting_notes_external_sent":
      return Number(m.failed) > 0
        ? res("warn", "alert", `${m.failed} failed`)
        : res("ok", "dot", "Sent");
    case "meeting_notes_teams_consent":
      return res("approved", "check", "Granted");
    case "meeting_notes_no_transcript":
      return res("skip", "skip", "No transcript");
    case "meeting_notes_opted_out":
      return res("neutral", "dot", "Opted out");
    case "meeting_notes_stopped":
      return res("neutral", "dot", "Stopped");
    case "memory_cleared":
      return res("neutral", "dot", "Cleared", m.byEmail);
    case "tick_skipped_business_hours":
      return res("neutral", "clock", "Paused");
    case "teams_chat_pruned_not_found":
      return res("neutral", "dot", "Cleaned up");
    case "brain_dedup": {
      const skipped = Math.max(0, (Number(m.proposed) || 0) - (Number(m.kept) || 0));
      return skipped
        ? res("skip", "skip", skipped === 1 ? "Skipped duplicate" : "Skipped duplicates")
        : res("neutral", "dot", "No duplicates");
    }
    case "brain_plan":
      return res("neutral", "dot", "Planned");
    case "brain_execute_summary":
      return Number(m.executedCount) > 0
        ? res("ok", "dot", "Done", m.approvedBy)
        : res("neutral", "dot", "Nothing run", m.approvedBy);
    default:
      break;
  }

  if (isFailureType(t)) return res("fail", "x", "Failed");
  if (isWarningType(t)) return res("warn", "alert", "Not delivered");
  if (isSkippedType(t)) {
    return isDuplicateReason(m.reason)
      ? res("skip", "skip", "Skipped duplicate")
      : t === "meeting_skipped" && lower(m.reason) === "meeting_no_slot_found"
        ? res("skip", "skip", "No open slot")
        : res("skip", "skip", "Skipped");
  }
  if (/_sent$/.test(t)) return res("ok", "dot", "Sent");
  if (isExecutedType(t)) return res("ok", "dot", "Done");
  return res("neutral", "dot", "Logged");
}

/* ----------------------------------------------------------------- badge */

// Type -> color family. Families, not types, so a new log type only has to
// pick one; AuditLogs.css carries each family's colors for both themes.
const BADGE_FAMILIES = {
  report_sent: "report",
  send_weekly_report: "report",
  report_duplicate_race: "waiting",

  task_created: "task",
  create_task: "task",
  project_created: "task",
  create_project: "task",
  project_skipped: "waiting",
  automation_created: "neutral",
  automation_built: "neutral",
  automation_switched: "neutral",
  automation_deleted: "neutral",
  automation_prompt_changed: "neutral",
  automation_trigger_changed: "neutral",
  automation_run_recovered: "task",
  automation_run_started: "task",
  automation_run_held: "neutral",
  automation_run_checked: "task",
  automation_run_done: "task",
  automation_run_needs_attention: "waiting",
  automation_run_denied: "neutral",
  automation_run_notice_sent: "ping",
  automation_sweep_error: "waiting",
  message_sent: "ping",
  task_updated: "taskUpdate",
  update_task: "taskUpdate",
  dod_blocked_completion: "waiting",

  ping_sent: "ping",
  send_ping: "ping",
  clarify_sent: "ping",
  ask_clarifying: "ping",
  email_reply_sent: "ping",

  escalation_sent: "escalation",
  send_escalation: "escalation",
  escalation_rerouted: "escalation",
  escalation_deferred_bh: "waiting",

  meeting_scheduled: "meeting",
  schedule_meeting: "meeting",
  meeting_updated: "meeting",
  meeting_cancelled: "meeting",
  meeting_change_skipped: "meeting",
  update_meeting: "meeting",
  cancel_meeting: "meeting",

  approval_pending: "waiting",
  approval_gate: "waiting",
  approval_executing: "approved",
  approval_executed: "approved",
  approval_warning: "waiting",
  approval_duplicate: "neutral",
  approval_denied: "neutral",
  approval_failed: "failed",
  approval_held: "neutral",
  approval_not_needed: "neutral",
  pilot_baseline_held: "neutral",
  pilot_automated: "neutral",
  holding_off: "neutral",
  email_reply_cap_reached: "waiting",

  brain_plan: "neutral",
  brain_dedup: "neutral",
  brain_execute_summary: "neutral",
  tick: "neutral",
  pm_state: "neutral",
  retro_built: "neutral",
};

export function badgeFamily(type) {
  const t = lower(type);
  if (BADGE_FAMILIES[t]) return BADGE_FAMILIES[t];
  if (isFailureType(t)) return "failed";
  if (isWarningType(t)) return "waiting";
  if (isSkippedType(t)) return "neutral";
  if (t.startsWith("meeting_notes_")) return "meeting";
  if (t.startsWith("memory_") || t.startsWith("tick") || t.startsWith("brain_")) {
    return "neutral";
  }
  return "default";
}

/* ---------------------------------------------------------------- search */

export function buildSearchText(log, extra = "") {
  const m = logMeta(log);
  return [
    log?.type,
    log?.title,
    log?.reason,
    log?.to,
    log?.owner,
    log?.ownerEmail,
    log?.assignee,
    m.reason,
    m.to,
    m.owner,
    m.ownerEmail,
    m.assignee,
    m.taskTitle,
    m.topic,
    JSON.stringify(m),
    extra,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}
